export default function ValueAdd() {
  const values = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Expert Guidance',
      description: 'Business strategy, go-to-market planning, and growth expertise to help you scale faster',
      color: 'from-pink-400 to-rose-500',
      badge: 'Strategy',
    },
    {
      icon: 'ri-team-line',
      title: 'Strong Networks',
      description: 'Access to strategic partnerships, industry connections, and key decision-makers',
      color: 'from-purple-400 to-indigo-500',
      badge: 'Network',
    },
    {
      icon: 'ri-funds-line',
      title: 'Fundraising Support',
      description: 'Investor connections, pitch preparation, and guidance through the fundraising process',
      color: 'from-teal-400 to-cyan-500',
      badge: 'Capital',
    },
    {
      icon: 'ri-rocket-line',
      title: 'Hands-on Incubation',
      description: 'Direct support to accelerate product development and business growth',
      color: 'from-orange-400 to-amber-500',
      badge: 'Growth',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Sustainable Business Focus',
      description: 'Building cash-flow-positive pet businesses with long-term potential',
      color: 'from-emerald-400 to-green-500',
      badge: 'Sustainability',
    },
  ];

  return (
    <section className="py-24 px-6 bg-gradient-to-b from-white to-[#F0FFF5]/30">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-[#FF6B6B] to-[#6B5FFF] bg-clip-text text-transparent mb-6">
            How We Help Founders Succeed
          </h2>
          <p className="text-lg text-[#4A5568] max-w-[800px] mx-auto leading-relaxed">
            We work closely with founders to fill key gaps and unlock new opportunities.
          </p>
        </div>

        {/* Content Layout */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Interactive Pet-Themed Illustration */}
          <div className="relative h-[600px] flex items-center justify-center">
            {/* Background Glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-pink-200/30 via-purple-200/30 to-teal-200/30 rounded-3xl blur-3xl"></div>
            
            {/* Central Pet Startup Scene */}
            <div className="relative z-10 w-full max-w-[500px]">
              {/* Main Illustration - Happy pets with startup growth elements */}
              <div className="relative bg-gradient-to-br from-pink-50 to-purple-50 rounded-3xl p-8 shadow-2xl">
                <img 
                  src="https://static.readdy.ai/image/45ee86dea452076dc7a3c34b683d6864/55e407138fe82b9be9979e10c3a891cd.png" 
                  alt="Pet Startup Success" 
                  className="w-full h-auto rounded-2xl"
                />
                
                {/* Orbiting Resource Icons with Pet Theme */}
                {/* Top Left - Expert Guidance */}
                <div className="absolute -top-6 -left-6 group cursor-pointer">
                  <div className="relative">
                    <div className="w-20 h-20 bg-gradient-to-br from-pink-400 to-rose-500 rounded-2xl flex items-center justify-center shadow-xl animate-float transform group-hover:scale-110 transition-transform duration-300">
                      <i className="ri-lightbulb-line text-3xl text-white"></i>
                    </div>
                    {/* Paw print accent */}
                    <div className="absolute -bottom-2 -right-2 text-2xl animate-pulse">🐾</div>
                  </div>
                </div>
                
                {/* Top Right - Networks */}
                <div className="absolute -top-6 -right-6 group cursor-pointer" style={{ animationDelay: '0.5s' }}>
                  <div className="relative">
                    <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-2xl flex items-center justify-center shadow-xl animate-float transform group-hover:scale-110 transition-transform duration-300">
                      <i className="ri-team-line text-3xl text-white"></i>
                    </div>
                    <div className="absolute -bottom-2 -right-2 text-2xl animate-pulse" style={{ animationDelay: '0.5s' }}>🐾</div>
                  </div>
                </div>
                
                {/* Middle Left - Capital */}
                <div className="absolute top-1/2 -translate-y-1/2 -left-8 group cursor-pointer" style={{ animationDelay: '1s' }}>
                  <div className="relative">
                    <div className="w-24 h-24 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-2xl flex items-center justify-center shadow-xl animate-float transform group-hover:scale-110 transition-transform duration-300">
                      <i className="ri-funds-line text-4xl text-white"></i>
                    </div>
                    <div className="absolute -bottom-2 -right-2 text-3xl animate-pulse" style={{ animationDelay: '1s' }}>🐾</div>
                  </div>
                </div>
                
                {/* Middle Right - Growth */}
                <div className="absolute top-1/2 -translate-y-1/2 -right-8 group cursor-pointer" style={{ animationDelay: '1.5s' }}>
                  <div className="relative">
                    <div className="w-24 h-24 bg-gradient-to-br from-orange-400 to-amber-500 rounded-2xl flex items-center justify-center shadow-xl animate-float transform group-hover:scale-110 transition-transform duration-300">
                      <i className="ri-rocket-line text-4xl text-white"></i>
                    </div>
                    <div className="absolute -bottom-2 -right-2 text-3xl animate-pulse" style={{ animationDelay: '1.5s' }}>🐾</div>
                  </div>
                </div>
                
                {/* Bottom Center - Sustainability */}
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 group cursor-pointer" style={{ animationDelay: '2s' }}>
                  <div className="relative">
                    <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-green-500 rounded-2xl flex items-center justify-center shadow-xl animate-float transform group-hover:scale-110 transition-transform duration-300">
                      <i className="ri-line-chart-line text-4xl text-white"></i>
                    </div>
                    <div className="absolute -bottom-2 -right-2 text-3xl animate-pulse" style={{ animationDelay: '2s' }}>🐾</div>
                  </div>
                </div>
                
                {/* Connecting Lines - Subtle dashed lines */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: -1 }}>
                  <line x1="10%" y1="10%" x2="50%" y2="50%" stroke="#FF6B6B" strokeWidth="2" strokeDasharray="5,5" opacity="0.3" className="animate-pulse" />
                  <line x1="90%" y1="10%" x2="50%" y2="50%" stroke="#6B5FFF" strokeWidth="2" strokeDasharray="5,5" opacity="0.3" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
                  <line x1="5%" y1="50%" x2="50%" y2="50%" stroke="#4ECDC4" strokeWidth="2" strokeDasharray="5,5" opacity="0.3" className="animate-pulse" style={{ animationDelay: '1s' }} />
                  <line x1="95%" y1="50%" x2="50%" y2="50%" stroke="#FFA500" strokeWidth="2" strokeDasharray="5,5" opacity="0.3" className="animate-pulse" style={{ animationDelay: '1.5s' }} />
                  <line x1="50%" y1="95%" x2="50%" y2="50%" stroke="#10B981" strokeWidth="2" strokeDasharray="5,5" opacity="0.3" className="animate-pulse" style={{ animationDelay: '2s' }} />
                </svg>
              </div>
              
              {/* Floating Pet Emojis */}
              <div className="absolute top-4 left-4 text-5xl opacity-20 animate-float">🐕</div>
              <div className="absolute top-8 right-8 text-4xl opacity-20 animate-float" style={{ animationDelay: '1s' }}>🐈</div>
              <div className="absolute bottom-12 left-8 text-3xl opacity-20 animate-float" style={{ animationDelay: '1.5s' }}>❤️</div>
              <div className="absolute bottom-8 right-12 text-4xl opacity-20 animate-float" style={{ animationDelay: '0.8s' }}>✨</div>
            </div>
          </div>

          {/* Right: Value Cards */}
          <div className="space-y-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-start gap-5">
                  {/* Icon */}
                  <div className={`w-14 h-14 bg-gradient-to-br ${value.color} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                    <i className={`${value.icon} text-2xl text-white`}></i>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-[#2D3748]">
                        {value.title}
                      </h3>
                      <span className={`px-3 py-1 bg-gradient-to-r ${value.color} text-white text-xs font-semibold rounded-full`}>
                        {value.badge}
                      </span>
                    </div>
                    <p className="text-sm text-[#4A5568] leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>

                {/* Accent Bar */}
                <div className={`h-1 bg-gradient-to-r ${value.color} rounded-full mt-4 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left`}></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}